import { useState } from 'react'
import { Plus, Edit2, Trash2, Briefcase, MapPin, Building2 } from 'lucide-react'
import BranchModal from './BranchModal'
import Toast from '../common/Toast'
import ConfirmDialog from '../common/ConfirmDialog'

export default function BranchesTab({ organizations, branches, onAdd, onUpdate, onDelete }) {
  const [showModal, setShowModal] = useState(false)
  const [editingBranch, setEditingBranch] = useState(null)
  const [orgFilter, setOrgFilter] = useState('all')
  const [toast, setToast] = useState(null)
  const [confirmingDelete, setConfirmingDelete] = useState(null) // branch object

  const filteredBranches = orgFilter === 'all'
    ? branches
    : branches.filter((b) => b.organizationId === orgFilter)

  function openAdd() { setEditingBranch(null); setShowModal(true) }
  function openEdit(branch) { setEditingBranch(branch); setShowModal(true) }
  function closeModal() { setShowModal(false); setEditingBranch(null) }

  async function handleConfirmDelete() {
    const branch = confirmingDelete
    setConfirmingDelete(null)
    const result = await onDelete(branch.id)
    setToast(result?.success === false
      ? { message: result.message, type: 'error' }
      : { message: `"${branch.name}" has been deleted.`, type: 'success' }
    )
  }

  async function handleSave(branchData) {
    if (editingBranch) {
      const result = await onUpdate(editingBranch.id, branchData)
      setToast(result?.success === false
        ? { message: result.message, type: 'error' }
        : { message: 'Branch updated successfully!', type: 'success' }
      )
    } else {
      const result = await onAdd(branchData)
      setToast(result?.success === false
        ? { message: result.message, type: 'error' }
        : { message: 'Branch created successfully!', type: 'success' }
      )
    }
    closeModal()
  }

  function getTankCount(branch) {
    return (branch.tanks || []).length
  }

  function getPumpCount(branch) {
    return (branch.pumps || []).length
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-gray-800">
          Branches
          <span className="ml-2 text-sm font-normal text-gray-500">({filteredBranches.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
          >
            <option value="all">All Organizations</option>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <button
            onClick={openAdd}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition text-sm flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Add Branch</span>
          </button>
        </div>
      </div>

      {/* Empty state */}
      {filteredBranches.length === 0 && (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Branches Found</h3>
          <p className="text-gray-400 text-sm mb-4">
            {orgFilter !== 'all'
              ? 'No branches in the selected organization.'
              : 'Add a branch to get started.'}
          </p>
          <button
            onClick={openAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition text-sm"
          >
            + Add Branch
          </button>
        </div>
      )}

      {/* Branch cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredBranches.map((branch) => {
          const org = organizations.find((o) => o.id === branch.organizationId)
          const tankCount = getTankCount(branch)
          const pumpCount = getPumpCount(branch)

          return (
            <div key={branch.id} className="bg-white rounded-xl shadow hover:shadow-md transition p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{branch.name}</h3>
                    <div className="flex items-center space-x-1 mt-0.5">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">{branch.location}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(branch)}
                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition"
                    title="Edit branch"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(branch)}
                    className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition"
                    title="Delete branch"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                {org && (
                  <div className="flex items-center space-x-1.5 text-xs text-gray-500">
                    <Building2 className="w-3.5 h-3.5" />
                    <span>{org.name}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2 text-xs">
                  <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                    {tankCount} tank{tankCount !== 1 ? 's' : ''}
                  </span>
                  <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                    {pumpCount} pump{pumpCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {showModal && (
        <BranchModal
          branch={editingBranch}
          organizations={organizations}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {confirmingDelete && (
        <ConfirmDialog
          message={
            (confirmingDelete.tanks || []).length > 0
              ? `Delete "${confirmingDelete.name}"? This will remove ${confirmingDelete.tanks.length} tank(s). This cannot be undone.`
              : `Delete "${confirmingDelete.name}"? This cannot be undone.`
          }
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmingDelete(null)}
        />
      )}
    </div>
  )
}
